import React, { useCallback } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';

import { useMessage } from '@tmapy/intl';
import { SvgReload, TertiaryBtn } from '@tmapy/style-guide';

import type { EpsiDocResponse } from '../../openapi/types';
import type { RouteId } from '../../routes';
import { useAppContext, useHistoryAction } from '../../hooks';
import { getCmdCtrlUrl } from '../../utils/getcmdctrlurl';

import { AttachmentIcon } from '../../docs/components/AttachmentIcon';
import { EPSI_LIST_COLUMN_COUNT, isEpsiRetryable } from './constants';
import { EpsiBadge } from './EpsiBadge';
import { EpsiRetryAlert } from './EpsiRetryAlert';
import { epsiMessages, epsiTypeMessage } from './epsiMessages';
import { type EpsiRetryState, epsiRetryQueryState } from './useEpsiRetry';

export type EpsiListItemProps = {
  data: EpsiDocResponse;
  /** Nepředané = uživatel na retry nemá právo, tlačítko se vůbec nenabídne. */
  onRetry?(id: number): void;
  /** Stav posledního retry této sady; nepředané = ještě se na ní neklikalo. */
  retryState?: EpsiRetryState;
  onRetryDismiss?(id: number): void;
};

export const EpsiListItem: React.FC<EpsiListItemProps> = ({
  data,
  onRetry,
  retryState,
  onRetryDismiss,
}) => {
  const { appType } = useAppContext();
  const formatMessage = useMessage();
  const routeToEpsiDetail = useHistoryAction('managerEpsiDetail', { id: data.id });

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      if (event.ctrlKey || event.metaKey) {
        window.open(
          getCmdCtrlUrl('managerEpsiDetail' as RouteId, { appType, id: data.id }),
          '_blank',
        );
        return; // Ponech nativní chování
      }

      const selectText = window.getSelection();
      if (!selectText || selectText.toString() === '') {
        routeToEpsiDetail();
      }
    },
    [routeToEpsiDetail],
  );

  /** Řádek je klikací na detail, akce v něm se nesmí propsat do navigace. */
  const handleRetryClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onRetry?.(data.id);
    },
    [onRetry, data.id],
  );

  const handleRetryAlertHide = useCallback(() => {
    onRetryDismiss?.(data.id);
  }, [onRetryDismiss, data.id]);

  const typeMessage = epsiTypeMessage(data.type);
  const canRetry = !!onRetry && isEpsiRetryable(data.state);

  return (
    <>
      <tr onClick={handleClick}>
        <td className='sg-a-ta-c'>
          <EpsiBadge state={data.state} />
        </td>
        <td className='sg-a-ta-c'>{data.id}</td>
        <td className='sg-a-ta-r'>
          {data.pspi_datum_od && (
            <time
              className='sg-u-type-sml sg-u-c-body-darkest sg-a-ws-nw'
              dateTime={data.pspi_datum_od}
            >
              <FormattedDate value={new Date(data.pspi_datum_od)} />
            </time>
          )}
        </td>
        <td>{typeMessage ? <FormattedMessage {...typeMessage} /> : data.type}</td>
        <td className='sg-a-ta-r'>
          {data.created && (
            <time className='sg-u-type-sml sg-u-c-body-darkest sg-a-ws-nw' dateTime={data.created}>
              <FormattedDate value={new Date(data.created)} />{' '}
              <FormattedTime
                value={new Date(data.created)}
                hour='numeric'
                minute='numeric'
                second='numeric'
              />
            </time>
          )}
          <br />
          {data.imported && (
            <time
              className='dmx-u-c-cta-red sg-u-type-sml sg-u-c-body-darkest sg-a-ws-nw'
              dateTime={data.imported}
            >
              <FormattedDate value={new Date(data.imported)} />{' '}
              <FormattedTime
                value={new Date(data.imported)}
                hour='numeric'
                minute='numeric'
                second='numeric'
              />
            </time>
          )}
        </td>
        <td className='sg-a-ta-l'>{data.source}</td>
        <td className='sg-a-w-0'>
          {data.attachments_count > 0 && <AttachmentIcon count={data.attachments_count} />}
        </td>
        {/* Vlastní sloupec, aby ikona přílohy zůstala napříč řádky zarovnaná. */}
        <td className='sg-a-w-0'>
          {canRetry && (
            <TertiaryBtn
              icon={{ element: <SvgReload /> }}
              tooltip={formatMessage(epsiMessages.retryBtn)}
              queryState={epsiRetryQueryState(retryState)}
              onClick={handleRetryClick}
            />
          )}
        </td>
      </tr>
      {retryState && retryState.phase !== 'waiting' && (
        /* Výsledek retry patří k tomuhle řádku, ne do hlavičky přehledu. */
        <tr className='dmx-a-cur-a'>
          <td colSpan={EPSI_LIST_COLUMN_COUNT}>
            <EpsiRetryAlert state={retryState} onHide={handleRetryAlertHide} />
          </td>
        </tr>
      )}
    </>
  );
};
